<template>
  <div id="app" class="min-h-dvh bg-base-200 text-base-content">
    <!-- Main Calculator -->
    <div class="mx-auto w-full max-w-md p-3">
      <div class="card bg-base-100 shadow-sm">
        <div class="card-title px-4 pt-4 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <button @click="showSettingsModal = true" class="btn btn-ghost btn-square btn-lg" title="Configurar Impuestos">⚙️</button>
            <span class="text-xl font-bold">🧮 Calculadora de Impuestos</span>
          </div>
          <button @click="toggleTheme" class="btn btn-ghost btn-square btn-lg" :title="isDark ? 'Modo claro' : 'Modo oscuro'">{{ isDark ? '☀️' : '🌙' }}</button>
        </div>

        <!-- Copy All Button -->
        <div class="px-4 pt-2">
          <button @click="copyAllValues" class="btn btn-primary btn-xl w-full">
            📋 Copiar Todo
          </button>
        </div>

        <!-- Unified Input Fields -->
        <div class="card-body gap-4 p-4">
          <!-- Subtotal First -->
          <label class="form-control w-full gap-1" :class="highlightClass('subtotal')">
            <span class="label-text text-xl font-semibold">📊 Subtotal (L)</span>
            <div class="join w-full">
              <input
                v-model="inputValues.subtotal"
                @focus="onFocus('subtotal')"
                @blur="onBlur('subtotal')"
                @input="onInput('subtotal', inputValues.subtotal)"
                @keydown.enter="$event.target.blur()"
                inputmode="decimal"
                class="input input-bordered input-xl w-full join-item text-right"
                type="text"
                placeholder="Ingrese el subtotal"
              />
              <button @click="copyValue('subtotal')" class="btn btn-square btn-primary join-item btn-xl shrink-0" title="Copiar Subtotal">📋</button>
            </div>
            <span v-if="focusedField === 'subtotal' && previousValues.subtotal" class="label-text-alt text-info">
              Anterior: L{{ previousValues.subtotal }}
            </span>
          </label>

          <!-- Tax Fields -->
          <div
            v-for="tax in enabledTaxes"
            :key="tax.id"
            class="form-control w-full gap-1"
            :class="highlightClass(tax.id)"
          >
            <label class="label py-1">
              <span class="label-text text-xl font-semibold">🏷️ Impuesto {{ tax.rate }}% (L)</span>
            </label>
            <div class="flex items-center gap-2">
              <button
                @click="toggleTax(tax.id)"
                class="btn btn-square btn-ghost btn-xl shrink-0"
                :title="`Eliminar impuesto ${tax.rate}%`"
              >
                ✕
              </button>
              <div class="join flex-1">
                <input
                  v-model="inputValues[tax.id]"
                  @focus="onFocus(tax.id)"
                  @blur="onBlur(tax.id)"
                  @input="onInput(tax.id, inputValues[tax.id])"
                  @keydown.enter="$event.target.blur()"
                  inputmode="decimal"
                  class="input input-bordered input-xl w-full join-item text-right"
                  type="text"
                  :placeholder="`Ingrese impuesto ${tax.rate}%`"
                />
                <button @click="copyValue(tax.id)" class="btn btn-square btn-primary join-item btn-xl shrink-0" :title="`Copiar Impuesto ${tax.rate}%`">📋</button>
              </div>
            </div>
            <span v-if="focusedField === tax.id && previousValues[tax.id]" class="label-text-alt text-info">
              Anterior: L{{ previousValues[tax.id] }}
            </span>
          </div>

          <!-- Visual Separator -->
          <div class="divider"></div>

          <!-- Total at Bottom -->
          <label class="form-control w-full rounded-box bg-primary/5 p-3 gap-1" :class="highlightClass('total')">
            <span class="label-text text-xl font-bold">💰 TOTAL (L)</span>
            <div class="join w-full">
              <input
                v-model="inputValues.total"
                @focus="onFocus('total')"
                @blur="onBlur('total')"
                @input="onInput('total', inputValues.total)"
                @keydown.enter="$event.target.blur()"
                inputmode="decimal"
                class="input input-bordered input-xl w-full join-item text-right font-bold text-lg text-success"
                type="text"
                placeholder="Ingrese el total"
              />
              <button @click="copyValue('total')" class="btn btn-square btn-primary join-item btn-xl shrink-0" title="Copiar Total">📋</button>
            </div>
            <span v-if="focusedField === 'total' && previousValues.total" class="label-text-alt text-info">
              Anterior: L{{ previousValues.total }}
            </span>
          </label>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-2 px-4 pb-4">
          <button @click="resetAllValues" class="btn btn-outline btn-error btn-xl flex-1">🗑️ Limpiar Todo</button>
        </div>
      </div>
    </div>

    <!-- Settings Modal -->
    <dialog v-if="showSettingsModal" class="modal" :open="showSettingsModal" @click="showSettingsModal = false" @keydown.escape="showSettingsModal = false">
      <div class="modal-box" @click.stop>
        <form method="dialog">
          <button @click="showSettingsModal = false" class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
        </form>
        <h3 class="text-lg font-bold">⚙️ Configurar Impuestos</h3>

        <div class="py-4">
          <h4 class="font-medium mb-3">Impuestos Disponibles</h4>
          <div class="flex flex-wrap gap-3">
            <label v-for="tax in availableTaxes" :key="tax.id" class="flex items-center gap-2">
              <input
                type="checkbox"
                class="checkbox checkbox-primary"
                :checked="tax.enabled"
                @change="toggleTax(tax.id)"
              />
              <span>{{ tax.rate }}%</span>
              <button
                v-if="tax.removable"
                @click="handleRemoveTax(tax.id)"
                class="btn btn-xs btn-ghost text-error"
                title="Eliminar impuesto"
              >
                🗑️
              </button>
            </label>
          </div>
        </div>

        <div class="border-t border-base-300 pt-4">
          <h4 class="font-medium mb-2">Agregar Impuesto Personalizado</h4>
          <div class="flex gap-2 items-end">
            <label class="form-control w-full">
              <input
                v-model="newTaxRate"
                type="number"
                placeholder="Porcentaje (ej: 12)"
                min="0"
                max="100"
                step="0.01"
                class="input input-bordered w-full"
                @keydown.enter="handleAddCustomTax"
              />
            </label>
            <button @click="handleAddCustomTax" class="btn btn-primary">
              ➕ Agregar
            </button>
          </div>
        </div>

        <div class="modal-action">
          <button @click="showSettingsModal = false" class="btn btn-ghost">Cerrar</button>
        </div>
      </div>
    </dialog>

    <!-- Notification -->
    <div v-if="notification.show" class="toast toast-top toast-center z-50">
      <div :class="['alert', alertClassMap[notification.type] || 'alert-warning']">
        <span>{{ notification.message }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useTaxCalculator } from './composables/useTaxCalculator'
import { useClipboard } from './composables/useClipboard'
import { useStorage } from './composables/useStorage'
import type { NotificationState } from './types'

// Theme management
const [isDark, setIsDark] = useStorage('theme', false)
const showSettingsModal = ref(false)

// Notification system
const notification = reactive<NotificationState>({
  show: false,
  message: '',
  type: 'success'
})

// Highlight helper — applies a themed ring on the last-edited field
const highlightClass = (field: string) => ({
  'rounded-box ring-2 ring-primary ring-offset-2 ring-offset-base-100': lastEditedField.value === field
})

// Alert class map — maps notification type to daisyUI color
const alertClassMap: Record<string, string> = {
  success: 'alert-success',
  error: 'alert-error',
}

// Tax calculator composable
const {
  availableTaxes,
  enabledTaxes,
  inputValues,
  previousValues,
  newTaxRate,
  lastEditedField,
  focusedField,
  onInput,
  onFocus,
  onBlur,
  toggleTax,
  addCustomTax,
  removeTax,
  resetAllValues,
  generateCopyAllText,
} = useTaxCalculator()

// Clipboard functionality
const { copy } = useClipboard()

// Theme toggle
const toggleTheme = () => {
  setIsDark(!isDark.value)
  document.documentElement.setAttribute('data-theme', isDark.value ? 'dark' : 'light')
}

// Shared helper for copy-with-notification pattern
const copyWithNotification = async (text: string, successMsg: string, emptyMsg: string) => {
  if (!text) {
    showNotification(emptyMsg, 'error')
    return
  }
  const success = await copy(text)
  if (success) {
    showNotification(successMsg, 'success')
  } else {
    showNotification('❌ Error al copiar', 'error')
  }
}

// Copy individual value
const copyValue = async (field: string) => {
  const value = inputValues[field]
  await copyWithNotification(value, `📋 Copiado: L${value}`, '❌ No hay valor para copiar')
}

// Copy all values
const copyAllValues = async () => {
  await copyWithNotification(generateCopyAllText(), '📋 Todos los valores copiados', '❌ No hay valores para copiar')
}

// Custom tax management for modal
const handleAddCustomTax = () => {
  const result = addCustomTax()
  showNotification(result.message, result.success ? 'success' : 'error')
  if (result.success) {
    showSettingsModal.value = false
  }
}

const handleRemoveTax = (taxId: string) => {
  const result = removeTax(taxId)
  showNotification(result.message, result.success ? 'success' : 'error')
}

// Notification system
const showNotification = (message: string, type: NotificationState['type'] = 'success') => {
  notification.message = message
  notification.type = type
  notification.show = true

  setTimeout(() => {
    notification.show = false
  }, 3000)
}

// Initialize theme
document.documentElement.setAttribute('data-theme', isDark.value ? 'dark' : 'light')
</script>

<style>
body {
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
</style>
